/**
 * Small utility helpers for the triage dashboard SPA.
 *
 * Kept intentionally minimal — add helpers only when they're used by
 * two or more components. One-off logic belongs inline in the page
 * that uses it.
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import { displayTimeZone, formatDateTime, getTimeZonePref } from "./timezone";

/**
 * Tailwind-aware `classNames` helper.
 *
 * Combines `clsx` (conditional class composition — objects, arrays,
 * falsy filtering) with `tailwind-merge` (deduplicates conflicting
 * utilities so `cn("px-2", "px-4")` resolves to just `"px-4"`).
 *
 * Usage:
 *
 * ```tsx
 * <div className={cn("px-2 py-1", isActive && "bg-blue-500", className)} />
 * ```
 *
 * Adapted from the shadcn/ui convention — widely used across the
 * React ecosystem, so the shape is familiar to anyone copy-pasting
 * component snippets.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * `s.slice(0, end)`, minus a high surrogate the cut would leave alone.
 *
 * `.slice` counts UTF-16 units, so cutting through an emoji (two units)
 * leaves half of it, which renders as "�". This keeps today's cut
 * point everywhere else: only a cut that lands inside a surrogate pair
 * comes back one unit shorter. (`truncateChars` in `jira-format.ts`
 * counts code points instead, which would move every cut on text that
 * holds an emoji earlier on; Galley #13975.)
 */
export function sliceUtf16Safe(s: string, end: number): string {
  const cut = s.slice(0, end);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

/**
 * Format an ISO-8601 timestamp as a short human-readable relative
 * time (e.g. "2h ago", "3d ago", "just now").
 *
 * Used by the analyses table to show how recent each row is without
 * eating a full cell with a 25-char datetime. Falls back to a short
 * absolute date for anything older than 30 days so the label stays
 * meaningful.
 *
 * Invalid inputs return the string unchanged so the UI doesn't
 * explode on malformed backend data — caller sees "garbage in,
 * garbage out" in the table rather than a blank cell.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;

  const now = Date.now();
  const deltaSecs = Math.round((now - then) / 1000);

  if (deltaSecs < 0) {
    // Future timestamp — clock skew. Treat as "just now" rather than
    // showing a negative value.
    //
    // NOTE: this branch is for PAST-expected timestamps only (e.g.
    // `arrived_at`, `completed_at`). For timestamps that are
    // LEGITIMATELY in the future (e.g. `presigned_urls_expire_at`,
    // which is `now + 7d` for fresh URLs), callers MUST use
    // `formatRelativeTimeBidirectional` instead — otherwise a
    // fresh 7-day-expiring URL renders as "Expires just now"
    // which looks like "already expired" to an operator. See
    // Bug 6 in the 2026-05-01 session for the root cause.
    return "just now";
  }
  if (deltaSecs < 45) return "just now";
  if (deltaSecs < 90) return "1m ago";

  const deltaMins = Math.round(deltaSecs / 60);
  if (deltaMins < 60) return `${deltaMins}m ago`;

  const deltaHours = Math.round(deltaMins / 60);
  if (deltaHours < 24) return `${deltaHours}h ago`;

  const deltaDays = Math.round(deltaHours / 24);
  if (deltaDays < 30) return `${deltaDays}d ago`;

  // Anything older — fall back to an absolute short date (YYYY-MM-DD).
  const d = new Date(then);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Format an ISO-8601 timestamp as a relative time string that
 * handles BOTH past and future timestamps correctly.
 *
 *   past:     "just now", "5m ago", "2h ago", "3d ago", or
 *             YYYY-MM-DD for anything older than 30 days
 *   future:   "in 5m", "in 2h", "in 7d"
 *
 * Added 2026-05-01 as the Bug 6 fix. Use this for any timestamp
 * that is LEGITIMATELY in the future (e.g.
 * `presigned_urls_expire_at` on fresh URLs, which is `now + 7d`).
 * `formatRelativeTime` remains the right helper for past-only
 * timestamps — it collapses future values to "just now" because
 * for `arrived_at` / `completed_at` / etc. a future value means
 * clock skew, not a legitimate future instant, and "just now" is
 * the least-surprising fallback.
 *
 * A thin wrapper that delegates to `formatRelativeTime` for past
 * timestamps so the past-side rendering stays identical across
 * the two helpers — refactoring one doesn't desync the other.
 */
export function formatRelativeTimeBidirectional(
  iso: string | null | undefined,
): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;

  const now = Date.now();
  const deltaSecs = Math.round((now - then) / 1000);

  // Past or within clock-skew tolerance (1 second) → delegate
  // to the past-only formatter so the two helpers stay in sync
  // on the past-side wording.
  if (deltaSecs >= -1) {
    return formatRelativeTime(iso);
  }

  // Future case — `then > now`. Compute positive "until" delta.
  const untilSecs = -deltaSecs;

  if (untilSecs < 45) return "in a moment";
  if (untilSecs < 90) return "in 1m";

  const untilMins = Math.round(untilSecs / 60);
  if (untilMins < 60) return `in ${untilMins}m`;

  const untilHours = Math.round(untilMins / 60);
  if (untilHours < 24) return `in ${untilHours}h`;

  const untilDays = Math.round(untilHours / 24);
  if (untilDays < 30) return `in ${untilDays}d`;

  // Far-future — fall back to the absolute short date. Mirrors
  // the past-side behavior so ops don't see a "months-away"
  // vague relative string where a concrete date is more useful.
  const d = new Date(then);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Format an ISO-8601 timestamp as a full absolute date+time in the
 * user's locale.
 *
 * Used on hover (`title` attribute) of relative-time cells so users
 * who want the precise timestamp can see it without losing the
 * compact relative display.
 *
 * Galley #21943: in the zone the top-bar switch chose (the viewer's own
 * by default, as before), and it always names that zone, e.g.
 * `10/23/2026, 6:08:00 PM GMT+2` or `… 4:08:00 PM UTC`.
 */
export function formatAbsoluteTime(iso: string | null | undefined): string {
  return formatDateTime(iso);
}

/**
 * TRIAG-320 — compact absolute date+time for inline row metadata, e.g.
 * `Jul 1, 4:48 PM` (this year) / `Jul 1 2025, 4:48 PM` (other years).
 *
 * Historical `commit_lifecycle.committed_at` rows carry only a DATE (the
 * pre-TRIAG-320 watcher parsed `--date=short`, which lands on midnight
 * UTC): for those, render the date alone rather than a misleading
 * midnight time. Detected by the timestamp being exactly 00:00:00 UTC —
 * a real commit landing on the exact midnight second is vanishingly rare
 * and merely renders date-only (never wrong data).
 */
export function formatCompactDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  const d = new Date(ts);
  const isMidnightUtc =
    d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  if (isMidnightUtc) {
    // Galley #15249: a date-only row's date is the UTC date it was stored
    // as. In the viewer's zone, midnight UTC is the previous evening west
    // of Greenwich, which named the day before.
    const sameUtcYear = d.getUTCFullYear() === new Date().getUTCFullYear();
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
      ...(sameUtcYear ? {} : { year: "numeric" }),
    });
  }
  // Galley #21943: in the zone the top-bar switch chose (the viewer's
  // own by default, as before).
  const utc = getTimeZonePref() === "utc";
  const now = new Date();
  const sameYear = utc
    ? d.getUTCFullYear() === now.getUTCFullYear()
    : d.getFullYear() === now.getFullYear();
  const timeZone = displayTimeZone();
  const datePart = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone,
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
  return `${datePart}, ${timePart}`;
}

/**
 * TRIAG-320 — human duration between two ISO timestamps, e.g. `9m 31s`,
 * `1h 04m`, `42s`. Returns `—` when either end is missing/unparseable or
 * the interval is negative (clock skew / partial rows).
 */
export function formatDurationBetween(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string {
  if (!startIso || !endIso) return "—";
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return "—";
  const secs = Math.round((end - start) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${String(secs % 60).padStart(2, "0")}s`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${String(mins % 60).padStart(2, "0")}m`;
}

/**
 * First 8 characters of an ID — matches the `short_id()` helper in
 * `src/lib.rs` on the Rust side, so what shows up in the dashboard
 * table matches what operators type into Slack (`/triage rush 18872899`).
 *
 * Iterates by character count (not byte-slicing) so non-ASCII input
 * doesn't panic. In practice analysis IDs are always UUIDs, but the
 * defensive version is a one-liner.
 */
export function shortId(id: string | null | undefined): string {
  if (!id) return "";
  return Array.from(id).slice(0, 8).join("");
}
